import { useState, useEffect, useRef, useContext, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { useReactToPrint } from 'react-to-print';
import GeneralModal from "../../helpers/GeneralModal";
import { createModal } from "react-modal-promise";
import LogicContext from "../../Context/LogicContext";
import { GetManualJournalAtom, SetManualJournalAtom, SetManualJournalEditAtom } from "../../store/manualJounalAtom";
import { ImAttachment } from "react-icons/im";
import { FaFilePdf } from "react-icons/fa";
import { MdDelete, MdEdit } from "react-icons/md";
import { IoPrintSharp } from "react-icons/io5";
import { accountService } from "../../services/accountService";
import ManualJournalDetailsContent from "./ManualJournalDetailsContent";
import ManualJournalScroll from "./ManualJournalScroll";

// Extract pageStyle to a constant to prevent recreation on each render
const PRINT_PAGE_STYLE = `
  @page {
    margin: 20mm;
  }
  body {
    font-family: Arial, sans-serif;
    line-height: 1.6;
    color: #333;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 20px 0;
  }
  th, td {
    border: 1px solid #ddd;
    padding: 12px 8px;
    text-align: left;
  }
  th {
    background-color: #f8f9fa;
    font-weight: bold;
    color: #495057;
  }
  .print-header {
    text-align: center;
    margin-bottom: 30px;
    border-bottom: 2px solid #007bff;
    padding-bottom: 20px;
  }
  .print-header h1 {
    color: #007bff;
    margin: 0;
    font-size: 28px;
  }
  .journal-info {
    background-color: #f8f9fa;
    padding: 15px;
    margin: 20px 0;
    border-left: 4px solid #007bff;
  }
  .status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: bold;
    text-transform: uppercase;
  }
  .amount-highlight {
    font-size: 18px;
    font-weight: bold;
    color: #28a745;
  }
  .text-right {
    text-align: right;
  }
  .text-center {
    text-align: center;
  }
  .mb-3 {
    margin-bottom: 1rem;
  }
  .mt-3 {
    margin-top: 1rem;
  }
  .border-top {
    border-top: 2px solid #dee2e6;
    padding-top: 15px;
    margin-top: 20px;
  }
  button, .btn, .dropdown {
    display: none !important;
  }
`;

function ManualJournalDetailsPage() {
  // Memoize modal to prevent recreation
  const myPromiseModal = useMemo(() => createModal(GeneralModal), []);
  
  const setJournalEdit = SetManualJournalEditAtom();
  const getManualJournalAtom = GetManualJournalAtom();
  const setManualJournalAtom = SetManualJournalAtom();
  const logicContext = useContext(LogicContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  
  const printableRef = useRef();
  const isMounted = useRef(false);
  
  // Memoize URL parsing
  const { currModuleId, currModuleName, currUserMenu } = useMemo(() => {
    const url = pathname.split("/");
    return {
      currModuleId: url[1],
      currModuleName: url[2],
      currUserMenu: url[3]
    };
  }, [pathname]);
  
  const [journalData, setJournalData] = useState({ details: {}, list: [] });
  const [isLoading, setIsLoading] = useState(true);

  // Memoize documentTitle to prevent recreation
  const documentTitle = useMemo(
    () => `Manual Journal - ${journalData.details?.account_ref_no || 'Details'}`,
    [journalData.details?.account_ref_no]
  );

  // Define useReactToPrint at the top level with useCallback
  const handlePrint = useReactToPrint({
    content: () => printableRef.current,
    documentTitle,
    pageStyle: PRINT_PAGE_STYLE
  });

  // Memoize callbacks to prevent child re-renders
  const addNewRecord = useCallback(() => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
  }, [navigate, currModuleId, currModuleName, currUserMenu]);

  const viewCustomerDetails = useCallback((id) => {
    navigate(`/${currModuleId}/${currModuleName}/customers/customer-details/${id}`);
  }, [navigate, currModuleId, currModuleName]);

  // Memoize API calls with debouncing
  const loadTableList = useCallback(async () => {
    if (!getManualJournalAtom.account_info?.length) {
      try {
        const res = await accountService.manualJournal();
        if (res.data && isMounted.current) {
          setManualJournalAtom(res.data);
        }
      } catch (error) {
        console.error('Error loading table list:', error);
      }
    }
  }, [getManualJournalAtom.account_info, setManualJournalAtom]);

  const loadDetails = useCallback(async (accountId) => {
    if (!accountId) return;
    
    setIsLoading(true);
    try {
      const response = await accountService.manualJournalDetails(accountId);
      if (response.data?.account_info?.length && isMounted.current) {
        const journal = response.data.account_info[0];
        setJournalData({ details: journal, list: response.data.account_info });
      }
    } catch (error) {
      console.error('Error loading details:', error);
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const editJournal = useCallback(async () => {
    const response = await myPromiseModal({
      title: "Modify Journal",
      body: "Do you want to modify this record?",
      showInput: false,
      placeHolder: "",
    });

    if (response.decision) {
      setJournalEdit(journalData.list);
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
    }
  }, [myPromiseModal, journalData.list, setJournalEdit, navigate, currModuleId, currModuleName, currUserMenu]);

  const repostManualJournal = useCallback(async () => {
    const response = await myPromiseModal({
      title: "Repost Journal",
      body: "Please confirm Journal Repost?",
      showInput: false,
      placeHolder: "",
    });
    
    if (response?.decision === false) {
      return;
    }
    
    try {
      const res = await accountService.rePostManualJournal(
        journalData?.details?.reference_number,
        journalData?.details?.transaction_type_id
      );
      const cleanResponse = logicContext.httpResponseManager(res.data?.[0] || res.data?.result_status?.[0]);
      logicContext.showToast(cleanResponse?.message_text, "success");
    } catch (err) {
      logicContext.showToast("Re-post failed!", 'error');
    }
  }, [myPromiseModal, journalData.details, logicContext]);

  const onDeleteJournal = useCallback(async () => {
    const response = await myPromiseModal({
      title: "Delete Journal",
      body: "Record will be deleted?",
      showInput: false,
      placeHolder: ""
    });

    if (response.decision) {
      try {
        const response = await accountService.deleteManualJournal(journalData.details?.account_ref_no);
        const data = logicContext.httpResponseManager(response.data[0]);
        const newAccList = getManualJournalAtom.account_info.filter(
          (acc) => acc.account_ref_no !== journalData.details?.account_ref_no
        );
        setManualJournalAtom({ ...getManualJournalAtom, account_info: newAccList });
        navigate(-1);
      } catch (error) {
        console.error('Error deleting journal:', error);
      }
    }
  }, [myPromiseModal, journalData.details, logicContext, getManualJournalAtom, setManualJournalAtom, navigate]);

  const actionPerform = useCallback(async (type, option) => {
    const data = { account_ref_no: id, option };

    const response = await myPromiseModal({
      title: type,
      body: `You want to ${type}`,
      showInput: false,
      placeHolder: ""
    });

    if (!response.decision) return;

    try {
      const resp = await accountService.journalAction(data);
      const result = logicContext.httpResponseManager(resp.data?.result_status[0]);
      logicContext.showToast(result?.message_text, 'success');

      switch (type) {
        case 'DELETE':
          const newAccList = getManualJournalAtom.account_info.filter(
            (acc) => acc.account_ref_no !== journalData.details?.account_ref_no
          );
          setManualJournalAtom({ ...getManualJournalAtom, account_info: newAccList });
          navigate(-1);
          break;
        case 'PUBLISH':
        case 'REVERSE':
          loadDetails(id);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error performing ${type} action:`, error);
    }
  }, [id, myPromiseModal, logicContext, getManualJournalAtom, setManualJournalAtom, journalData.details, navigate, loadDetails]);

  // Optimize useEffect with proper dependencies
  useEffect(() => {
    isMounted.current = true;
    document.title = 'Manual Journal Details';
    
    // Only load details if ID changed
    if (id) {
      loadDetails(id);
    }

    return () => {
      isMounted.current = false;
    };
  }, [id, loadDetails]);

  useEffect(() => {
    // Only load table list if needed and component is mounted
    if (isMounted.current) {
      loadTableList();
    }
  }, [loadTableList]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <section>
      <section className="flex flex-wrap justify-between">
        <div className="mt-3"></div>
        <div className="flex flex-row flex-wrap items-center">
          <div className="mt-3 flex flex-row items-center mr-6">
            <button 
              style={{ backgroundColor: "#476EF8" }} 
              onClick={repostManualJournal} 
              className="btn m-4 text-white"
            >
              Repost
            </button>
            <button 
              style={{ backgroundColor: "#476EF8" }} 
              onClick={addNewRecord} 
              className="btn m-4 text-white"
            >
              Add New
            </button>
            <Dropdown style={{ width: "150px", marginRight: "30px", marginTop: "2px" }}>
              <Dropdown.Toggle className="pr-10" id="dropdown-basic" style={{ backgroundColor: "#476EF8" }}>
                ACTION
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {journalData.details?.status_id === 1 && (
                  <Dropdown.Item 
                    onClick={() => actionPerform('PUBLISH', 5)} 
                    className="border" 
                    style={{ fontSize: "15px" }}
                  >
                    PUBLISH
                  </Dropdown.Item>
                )}
                <Dropdown.Item 
                  onClick={() => actionPerform('REVERSE', 6)} 
                  className="border" 
                  style={{ fontSize: "15px" }}
                >
                  REVERSE
                </Dropdown.Item>
                <Dropdown.Item 
                  onClick={() => actionPerform('DELETE', 3)} 
                  className="border" 
                  style={{ fontSize: "15px" }}
                >
                  DELETE
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div className="mt-3 flex flex-row items-center space-x-1">
            <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
              <ImAttachment className="h-[20px] w-[20px] flex-shrink-0 text-gray-600" />
            </div>
            <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
              <FaFilePdf onClick={handlePrint} className="h-[20px] w-[20px] flex-shrink-0 text-gray-600" />
            </div>
            <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
              <IoPrintSharp onClick={handlePrint} className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
            </div>
            <div className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
              <MdEdit onClick={editJournal} className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
            </div>
            <div onClick={onDeleteJournal} className="p-1.5 rounded-md hover:bg-gray-200 active:bg-gray-100 cursor-pointer">
              <MdDelete className="h-[23px] w-[23px] flex-shrink-0 text-gray-600" />
            </div>
          </div>
        </div>
      </section>
      <section className="lg:flex space-x-1">
        <div className="lg:w-[210px]">
          <ManualJournalScroll />
        </div>
        <section className="w-full overflow-x-hidden lg:mt-0">
          <div ref={printableRef}>
            <ManualJournalDetailsContent
              viewCustomerDetails={viewCustomerDetails}
              detailsList={journalData.list}
              id={id}
              details={journalData.details}
            />
          </div>
        </section>
      </section>
    </section>
  );
}

export default ManualJournalDetailsPage;